/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React from 'react';

//@Packages
import { useTranslation } from 'react-i18next';
import { Button, CardContent, Card, CardHeader, Divider, AlertCircleIcon, StatusBadge, ContentLoader, BadgeColor } from "@forge/common";

//@Utils
import { LOCALE_CONST, NO_HOST_REF_KEY } from '../../utils/constants';

//@Styles
import "./styles.scss";

//@Interfaces
interface DeviceData {
	[key: string]: string | number | boolean | undefined | null | [{
		[key: string]: string | number | boolean | undefined | null;
	}];
}

interface DataCardProps {
	title: string;
	subtitle?: string;
	onClick?: () => void;
	buttonTitle?: string;
	deviceData: DeviceData[];
	isButtonVisible?: boolean;
	gatewayPortUI?: React.ReactNode; // Added a new prop for custom UI port table
	enableLoader?: boolean;
	getColorClsName?: (status: string) => string; // Function prop for color class name
	getStatusValue?: (status: string) => string; // Function prop for status value
}

export const DataCard: React.FC<DataCardProps> = (
	{ deviceData, title, subtitle, onClick, buttonTitle, isButtonVisible, gatewayPortUI, enableLoader, getColorClsName, getStatusValue }
) => {
	const { t } = useTranslation();

	const renderValueComp = (data: DeviceData) => {
		if (data.hrefUrl === NO_HOST_REF_KEY) {
			return <div>{t(LOCALE_CONST.noSecureConnectEBIURL)}</div>;
		} else if (data.hrefUrl) {
			return (
				<a href={String(data.hrefUrl)} target='_blank' rel='noopener noreferrer' className='data-card-href-url-cls'>
					{String(data.hrefUrl)}
				</a>
			);
		} else if (Array.isArray(data.value)) {
			return data.value.map((item) => Object.entries(item)
				.map(([key, value]) => `${value}`)
				.join(', ')).join(', ');
		}
		else if (data.uiValue) {
			return (
				<div className='data-card-status-container'>
					<StatusBadge className={`mr-1 text-[0.5rem]`}
						color={getColorClsName(typeof data.uiValue === 'string' ? data.uiValue : '') as BadgeColor}
					/>
					<span className='ui-value-text'>
						{getStatusValue(typeof data.uiValue === 'string' ? data.uiValue : '')}
					</span>
				</div>
			)
		}
		else if (data.value === undefined || data.value === null) {
			return <span className='data-card-no-value-text'>-</span>;
		}

		else {
			return String(data.value);
		}
	};

	return (
		<Card className="mx-auto min-w-full max-w-4xl reusable-tab-card-container">
			<div className='card-header-container'>
				<CardHeader title={title} subtitle={subtitle} className="card-header-title" />
				{isButtonVisible && (
					<Button size="regular" variant="primary"
						iconPosition="left" onClick={onClick}
						className='data-card-button' aria-label='custombutton'
					>
						{buttonTitle}
					</Button>
				)}
			</div>
			<CardContent className='data-card-container'>
				{enableLoader ? (
					// Render ContentLoader for the content section
					<div className="data-card-loader">
						<ContentLoader />
					</div>
				) : (
					<>
						{deviceData.map((data: DeviceData, index: number) => (
							<div className="reusable-info-container" key={index}>
								<div className="reusable-info-cell">
									{!data.hideRow &&
									 (
										<>
											<div className="reusable-info-label">{String(data.label)}
												{data.isAlertIconVisible && <AlertCircleIcon className='data-card-alert-circle-icon' />}
											</div>

											<div className="reusable-info-value">
												{renderValueComp(data)}
											</div>
										</>
									)}
								</div>
								{index < deviceData.length - 1 && <Divider />}
							</div>
						))}
						{/* Render footerContent if provided */}
						{gatewayPortUI && (
							<div className="data-card-footer">
								{gatewayPortUI}
							</div>
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
};